import { TOWN_FLOOR_PLAN, type Point2 } from "@town/contracts";
import { HEIGHT_ROWS_PER_UNIT, texelPhase, type PixelViewport } from "../scene/pixelProjection";

/** One world unit is one Gather-style map tile of 32 texture pixels. */
export const PIXELS_PER_UNIT = 32;

/** The drawn map is the default floor plan's campus: building, gardens and walks. */
export const MAP_WORLD = {
  minX: TOWN_FLOOR_PLAN.campus.x0,
  maxX: TOWN_FLOOR_PLAN.campus.x1,
  minZ: TOWN_FLOOR_PLAN.campus.z0,
  maxZ: TOWN_FLOOR_PLAN.campus.z1,
} as const;

export const MAP_PIXEL_WIDTH = (MAP_WORLD.maxX - MAP_WORLD.minX) * PIXELS_PER_UNIT;
export const MAP_PIXEL_HEIGHT = (MAP_WORLD.maxZ - MAP_WORLD.minZ) * PIXELS_PER_UNIT;

/**
 * Map pixel rows per world unit of height. The office camera puts a point
 * (x, y, z) on map row 32z - 20y, so a sprite rises 20 rows per unit.
 */
export const HEIGHT_PIXELS_PER_UNIT = HEIGHT_ROWS_PER_UNIT * PIXELS_PER_UNIT;

export type PixelPoint = Readonly<{ x: number; y: number }>;

export function worldToPixel(point: Point2): PixelPoint {
  return {
    x: (point.x - MAP_WORLD.minX) * PIXELS_PER_UNIT,
    y: (point.z - MAP_WORLD.minZ) * PIXELS_PER_UNIT,
  };
}

export function pixelToWorld(point: PixelPoint): Point2 {
  return {
    x: point.x / PIXELS_PER_UNIT + MAP_WORLD.minX,
    z: point.y / PIXELS_PER_UNIT + MAP_WORLD.minZ,
  };
}

export function unitsToPixels(units: number): number {
  return Math.round(units * PIXELS_PER_UNIT);
}

/**
 * The canvas buffer pixel of the map's top-left corner, where the runtime's
 * map transform starts. Floor and wall texels then land on the same buffer
 * pixels as in the 3D upscale. At d = 16 the camera focus carries a 1/64
 * phase, so the corner moves by that phase onto a whole buffer pixel. Furniture
 * and people move with it, a quarter texel, too little to see.
 */
export function mapBufferOrigin(viewport: Pick<PixelViewport, "density" | "worldToBuffer">): PixelPoint {
  const phase = texelPhase(viewport.density);
  const origin = viewport.worldToBuffer(MAP_WORLD.minX + phase, 0, MAP_WORLD.minZ + phase);
  // Whole by construction; rounding only drops floating-point noise.
  return { x: Math.round(origin.x), y: Math.round(origin.y) };
}
